import { motion } from 'framer-motion';
import { Calendar, CheckCircle, Clock } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT

import { Service } from '../../../shared/hooks/useServiceSelection';
import LoadingComponent from '../../../shared/components/LoadingComponent';
import FooterNavigation from '../../../shared/components/Navigation';
import { useDateTimeSelection } from '../../../shared/hooks/useDateTimeSelection';

interface CompactDateSelectionProps {
  selectedServices: Service[];
  selectedTimeZone: string;
  selectedLocation: any;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  selectedTime: string | null;
  setSelectedTime: (time: string | null) => void;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: any;
}

const CompactDateSelection: React.FC<CompactDateSelectionProps> = ({
  selectedServices,
  selectedTimeZone,
  selectedLocation,
  selectedDate: propSelectedDate,
  setSelectedDate: propSetSelectedDate,
  selectedTime: propSelectedTime,
  setSelectedTime: propSetSelectedTime,
  onNext,
  onPrev,
  nodeData
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Extract settings from nodeData
  const settings = nodeData?.settings || {};
  const colors = settings?.colors || {
    cardBackground: '#000000',
    cardBorder: 'rgba(221,184,137,0.1)',
    titleText: '#DDB889',
    subtitleText: '#DDB889',
    iconColor: '#DDB889',
    timeslotBackground: 'rgba(0,0,0,0.95)',
    timeslotBorder: 'rgba(221,184,137,0.1)',
    cardBorderSelected: 'rgba(221,184,137,0.5)',
    selectionBackground: '#DDB889',
    selectionIcon: '#000000',
    buttonBackground: '#DDB889',
    buttonText: '#000000'
  };

  // Define custom scrollbar styles
  const scrollbarStyles = `
    .lp-custom-scrollbar::-webkit-scrollbar {
      width: 8px;
    }
    .lp-custom-scrollbar::-webkit-scrollbar-track {
      background: ${colors.cardBackground};
      border-radius: 4px;
    }
    .lp-custom-scrollbar::-webkit-scrollbar-thumb {
      background: ${colors.cardBorder};
      border-radius: 4px;
    }
    .lp-custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: ${colors.titleText};
    }
  `;

  // Keep selected option in local state
  const [selectedDateTimeOption, setSelectedDateTimeOption] = useState<any>(null);
  // Loading indicator for more dates
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  // Maintain a history of loaded dates and their time slots
  const [dateHistory, setDateHistory] = useState<{
    date: Date;
    timeSlots: string[];
  }[]>([]);

  // Use the hook for date/time data
  const {
    fetching: isLoadingAllTimeSlots,
    timeGroups,
    hasAvailableSlots,
    setSelectedDate: hookSetSelectedDate,
    formatDate
  } = useDateTimeSelection({
    selectedServices,
    selectedTimeZone,
    selectedLocation
  });

  // Format display date function
  const formatDisplayDate = (date: Date): string => {
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get all time slots from timeGroups
  const getAllTimeSlots = () => {
    if (!timeGroups) return [];

    // Combine all time slots from morning, afternoon, and evening
    return [
      ...(timeGroups.morning || []),
      ...(timeGroups.afternoon || []),
      ...(timeGroups.evening || [])
    ].sort();
  };

  // Update date history when time groups change
  useEffect(() => {
    if (propSelectedDate && timeGroups) {
      const allSlots = [
        ...(timeGroups.morning || []),
        ...(timeGroups.afternoon || []),
        ...(timeGroups.evening || [])
      ].sort();

      // Check if this date is already in history
      const dateExists = dateHistory.some(item =>
        item.date.toDateString() === propSelectedDate.toDateString()
      );

      if (!dateExists) {
        setDateHistory(prev => [
          ...prev,
          {
            date: new Date(propSelectedDate),
            timeSlots: allSlots
          }
        ]);
      }
    }
  }, [propSelectedDate, timeGroups, dateHistory]);

  // Set initial date when component mounts
  useEffect(() => {
    if (!propSelectedDate) {
      const initialDate = new Date();
      propSetSelectedDate(initialDate);
      hookSetSelectedDate(initialDate);
    }
  }, [propSelectedDate, propSetSelectedDate, hookSetSelectedDate]);

  // Handle show more button click - load more dates
  const handleShowMore = () => {
    setIsLoadingMore(true);

    // Get the current date
    const currentDate = propSelectedDate || new Date();

    // Calculate the next date (current date + 1 day)
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 1);

    // Set the date to fetch slots
    propSetSelectedDate(nextDate);
    hookSetSelectedDate(nextDate);

    // Reset loading after a short delay
    setTimeout(() => {
      setIsLoadingMore(false);
    }, 500);
  };

  // Handle time slot selection
  const handleTimeSlotSelect = (date: Date, timeSlot: string) => {
    // Create a date object with the selected date and time
    const [hours, minutes] = timeSlot.split(':').map(Number);
    const dateWithTime = new Date(date);
    dateWithTime.setHours(hours, minutes, 0, 0);

    setSelectedDateTimeOption({ id: `${formatDate(date)}-${timeSlot}`, date: dateWithTime });
    propSetSelectedDate(dateWithTime);
    propSetSelectedTime(timeSlot);
    hookSetSelectedDate(dateWithTime);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lp-w-full lp-transform lp-scale-100"
    >
      <style>{scrollbarStyles}</style>
      <div
        className="lp-rounded-xl lp-overflow-hidden lp-p-4"
        style={{
          backgroundColor: colors.cardBackground,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: colors.cardBorder
        }}
      >
        <div className="lp-flex lp-items-center lp-justify-center lp-mb-4">
          <h3
            className="lp-text-lg lp-font-medium lp-text-center lp-flex lp-items-center"
            style={{ color: colors.titleText }}
          >
            <Calendar className="lp-mr-2" size={18} style={{ color: colors.iconColor }} />
            {t('booking.selectDateTime')} {/* CHANGED: "Select Date and Time" */}
          </h3>
        </div>

        {/* Date Time Options */}
        {isLoadingAllTimeSlots ? (
          <div className="lp-flex lp-items-center lp-justify-center lp-py-8" style={{ minHeight: '400px' }}>
            <LoadingComponent />
          </div>
        ) : dateHistory.length === 0 ? (
          <div className="lp-text-center lp-py-8" style={{ color: colors.titleText }}>
            {t('datetime.noDatesLoaded')} {/* CHANGED: "No dates loaded yet." */}
          </div>
        ) : (
          <div
            className="lp-overflow-y-auto lp-overflow-x-hidden lp-custom-scrollbar"
            style={{
              maxHeight: '400px',
              scrollbarWidth: 'thin',
              scrollbarColor: `${colors.titleText} ${colors.cardBackground}`,
              padding: '0 4px' // Add a bit of padding for scrollbar spacing
            }}
          >
            {dateHistory.map((dateItem, dateIndex) => (
              <div key={`date-${dateIndex}`} className="lp-mb-4">
                {/* Date header */}
                <div
                  className="lp-p-2 lp-mb-2 lp-rounded-lg lp-sticky lp-top-0 lp-z-10 lp-backdrop-blur-sm"
                  style={{
                    backgroundColor: `${colors.cardBackground}F0`, // Adding transparency
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: colors.cardBorder,
                    color: colors.titleText,
                    boxShadow: `0 2px 4px rgba(0,0,0,0.1)`
                  }}
                >
                  <div className="lp-flex lp-items-center">
                    <Calendar className="lp-mr-2" size={14} style={{ color: colors.iconColor }} />
                    <span className="lp-font-medium">
                      {formatDisplayDate(dateItem.date)}
                    </span>
                  </div>
                </div>

                {/* Time slots */}
                <div className="lp-grid lp-grid-cols-1 lp-gap-2">
                  {dateItem.timeSlots.length === 0 ? (
                    <div className="lp-text-center lp-py-2" style={{ color: colors.subtitleText }}>
                      {t('datetime.noAvailableTimeSlotsForDate')} {/* CHANGED: "No available time slots for this date." */}
                    </div>
                  ) : (
                    dateItem.timeSlots.map((timeSlot, timeIndex) => {
                      const isSelected = selectedDateTimeOption?.id === `${formatDate(dateItem.date)}-${timeSlot}`;

                      return (
                        <motion.div
                          key={`time-${dateIndex}-${timeIndex}`}
                          className="lp-relative lp-p-3 lp-rounded-lg lp-flex lp-items-center lp-cursor-pointer"
                          style={{
                            backgroundColor: isSelected ? `${colors.timeslotBackground || colors.cardBackground}` : colors.timeslotBackground,
                            borderWidth: 1,
                            borderStyle: 'solid',
                            borderColor: isSelected ? colors.selectedTimeslotBackground : colors.timeslotBorder,
                            color: isSelected ? colors.titleText : colors.subtitleText
                          }}
                          onClick={() => handleTimeSlotSelect(dateItem.date, timeSlot)}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="lp-flex lp-items-center lp-flex-grow">
                            <Clock className="lp-mr-2" size={14} style={{ color: colors.iconColor }} />
                            <span>{timeSlot}</span>
                          </div>

                          {isSelected && (
                            <div
                              className="lp-w-6 lp-h-6 lp-rounded-full lp-flex lp-items-center lp-justify-center"
                              style={{ backgroundColor: colors.selectionBackground }}
                            >
                              <CheckCircle className="lp-w-4 lp-h-4" style={{ color: colors.selectionIcon }} />
                            </div>
                          )}
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Show More Button */}
        <div className="lp-flex lp-justify-center lp-mb-4 lp-mt-2">
          <motion.button
            className="lp-px-4 lp-py-2 lp-text-xs lp-rounded-lg lp-flex lp-items-center lp-justify-center"
            style={{
              backgroundColor: colors.buttonBackground,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: colors.buttonBackground,
              color: colors.buttonText,
              minWidth: '140px',
              minHeight: '36px' // Add this line to maintain button height
            }}
            onClick={handleShowMore}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <div className="lp-flex lp-items-center">
                <span className="lp-mr-2">{t('common.loading')}</span> {/* CHANGED: "Loading..." */}
                <div className="lp-w-4 lp-h-4 lp-border-2 lp-border-t-transparent lp-border-white lp-rounded-full lp-animate-spin"></div>
              </div>
            ) : (
              t('datetime.loadMoreDates') /* CHANGED: "Load More Dates" */
            )}
          </motion.button>
        </div>

        {/* Footer Navigation */}
        <FooterNavigation
          onPrev={onPrev}
          onNext={onNext}
          disabled={!selectedDateTimeOption}
        />
      </div>
    </motion.div>
  );
};

export default CompactDateSelection;
